import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from './ui/button';
import { useScrollToSection } from '../hooks/useScrollToSection';
import { useActiveSection } from '../hooks/useActiveSection';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollToSection } = useScrollToSection();

  const sectionIds = ['hero', 'stages', 'about', 'services', 'location', 'contact'];
  const activeSection = useActiveSection(sectionIds);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (id: string) => {
    scrollToSection(id);
    setMobileMenuOpen(false);
  };

  const navItems = [
    { label: 'Inicio', id: 'hero' },
    { label: 'Etapas', id: 'stages' },
    { label: 'Sobre Nosotros', id: 'about' },
    { label: 'Servicios', id: 'services' },
    { label: 'Ubicación', id: 'location' },
  ];

  const textTone = 'text-slate-900';

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ 
        y: 0,
        backgroundPosition: ['0px 0px', '48px 48px']
      }}
      transition={{
        y: { duration: 0.5 },
        backgroundPosition: { 
          duration: 20, 
          repeat: Infinity, 
          ease: "linear" 
        }
      }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/98 border-b border-primary/20 shadow-lg backdrop-blur-xl py-3'
          : 'bg-white/90 border-b border-primary/5 backdrop-blur-lg py-6'
      }`}
      style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(25, 40, 113, 0.04) 1px, transparent 0)',
        backgroundSize: '24px 24px'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-28">
          <motion.button
            type="button"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => scrollToSection('hero')}
            className="flex items-center"
          >
            <div className="relative overflow-hidden h-14 md:h-24 w-44 md:w-72 flex items-center justify-start">
              <img 
                src="/logo.png" 
                alt="Free Zone Logistics Park" 
                className="absolute left-[-5%] h-[150%] w-auto max-w-none object-contain transition-all duration-300 group-hover:scale-105" 
              />
            </div>
          </motion.button>

          <nav className="hidden md:flex items-center gap-1" role="navigation" aria-label="Navegación principal">
            {navItems.map((item, index) => {
              const isActive = activeSection === item.id;
              return (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * index }}
                  onClick={() => handleNavClick(item.id)}
                  className={`px-5 py-2 text-sm font-medium tracking-wide transition-colors rounded-full relative group ${
                    isActive ? 'text-white' : 'text-slate-600 hover:text-primary hover:bg-primary/5'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="relative z-10">{item.label}</span>
                  {isActive && (
                    <motion.div
                      layoutId="active-pill"
                      className="absolute inset-0 bg-primary rounded-full shadow-md z-0"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </motion.button>
              );
            })}

            <div className="ml-4 flex items-center">
              <Button
                onClick={() => handleNavClick('contact')}
                className={`relative px-6 rounded-full transition-all duration-500 ${
                  activeSection === 'contact' 
                    ? 'bg-primary text-white shadow-lg scale-105' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {activeSection === 'contact' && (
                  <motion.div
                    layoutId="active-pill"
                    className="absolute inset-0 bg-primary rounded-full z-0"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10">Contáctanos</span>
              </Button>
            </div>
          </nav>

          <button
            type="button"
            className={`md:hidden p-2 rounded-lg text-slate-900 hover:bg-slate-100`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-200 overflow-hidden"
          >
            <nav className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleNavClick(item.id)}
                    className="block w-full text-left px-4 py-3 rounded-lg text-primary hover:bg-primary/10 transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
                <Button onClick={() => handleNavClick('contact')} className="w-full bg-primary hover:bg-primary/90 text-white">
                  Contáctanos
                </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
